import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { RxCross2 } from "react-icons/rx";
import { LuUpload } from "react-icons/lu";
import { toast } from 'react-toastify';
import { useDestinationPopupMutation } from "../../../../services/Api";
import ToggleSwitch from "../../../../components/toggleSwitch";

function DestinationPopup({ isOpen, onClose }) {
  const [imagePreview, setImagePreview] = useState(null);
  const [destinationPopup, { isLoading }] = useDestinationPopupMutation();

  const validationSchema = Yup.object().shape({
    description: Yup.string(),
    countryName: Yup.string().required("Country name is required"),
    stateName: Yup.string().required("State name is required"),
    cityName: Yup.string().required("City name is required"),
    image: Yup.mixed()
      .required("Image is required")
      .test("fileSize", "Image must be less than 5MB", (value) => {
        if (!value || !value[0]) return true;
        return value[0].size <= 5 * 1024 * 1024;
      })
      .test("fileType", "Only JPG and PNG are allowed", (value) => {
        if (!value || !value[0]) return true;
        return ["image/jpeg", "image/png"].includes(value[0].type);
      }),
    active: Yup.boolean().default(true),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
    defaultValues: {
      active: true,
    },
  });

  // Watch the active value from the form
  const isActive = watch("active");

  const onSubmit = async (values) => {
    try {
      const formData = new FormData();
      const data = {
        description: values.description || "",
        stateName: values.stateName,
        cityName: values.cityName,
        countryName: values.countryName,
        status: values.active ? "ACTIVE" : "INACTIVE",
      };

      // Append all data fields to FormData
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });

      // Append image last
      if (values.image[0]) {
        formData.append("image", values.image[0]);
      }

      // Send the request with wrapped data object
      const response = await destinationPopup({ data: formData }).unwrap();

      if (response.success) {
        toast.success("Destination added successfully!");
        reset();
        setImagePreview(null);
        onClose();
      } else {
        toast.error(response.message || "Failed to add destination");
      }
    } catch (error) {
      console.error("Error adding destination:", error);
      toast.error(
        error?.data?.message || "Failed to add destination. Please try again."
      );
    }
  };

  if (!isOpen) return null;

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleToggleChange = (checked) => {
    setValue("active", checked);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg p-10 relative border border-[#EBE8FF]">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <RxCross2 size={24} />
        </button>

        <h2 className="mb-6 text-center font-roboto-mono text-2xl font-bold">
          Destination
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium font-roboto-mono text-gray-700 mb-1">
              Description
            </label>
            <input
              {...register("description")}
              className="w-full px-3 py-2 text-[#1F2937AB] border border-[#E1E1E1] h-[40px] rounded-md active:outline-none focus:outline-none active:border-[#D9DCB0] focus:border-[#D9DCB0]"
              placeholder="Enter description"
            />
            {errors.description && (
              <p className="text-errorcolor text-sm mt-1">
                {errors.description.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-roboto-mono font-medium text-gray-700 mb-1">
              Country Name
            </label>
            <input
              {...register("countryName")}
              className="w-full px-3 py-2 text-sm text-[#1F2937AB] border border-[#E1E1E1] h-[40px] rounded-md active:outline-none focus:outline-none active:border-[#D9DCB0] focus:border-[#D9DCB0]"
              placeholder="Enter country name"
            />
            {errors.countryName && (
              <p className="text-errorcolor text-sm mt-1">
                {errors.countryName.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-roboto-mono font-medium text-gray-700 mb-1">
              State Name
            </label>
            <input
              {...register("stateName")}
              className="w-full px-3 py-2 text-sm text-[#1F2937AB] border border-[#E1E1E1] h-[40px] rounded-md active:outline-none focus:outline-none active:border-[#D9DCB0] focus:border-[#D9DCB0]"
              placeholder="Enter state name"
            />
            {errors.stateName && (
              <p className="text-errorcolor text-sm mt-1">
                {errors.stateName.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-roboto-mono font-medium text-gray-700 mb-1">
              City Name
            </label>
            <input
              {...register("cityName")}
              className="w-full text-sm px-3 py-2 border text-[#1F2937AB] border-[#E1E1E1] h-[40px] rounded-md active:outline-none focus:outline-none active:border-[#D9DCB0] focus:border-[#D9DCB0]"
              placeholder="Enter city name"
            />
            {errors.cityName && (
              <p className="text-errorcolor text-sm mt-1">
                {errors.cityName.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-roboto-mono font-medium text-gray-700 mb-1">
              Image
            </label>
            <div className="input_div">
              <div className="relative w-full">
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  {...register("image")}
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {imagePreview && (
                  <div className="mb-4">
                    <img
                      src={imagePreview}
                      alt="Selected Preview"
                      className="mt-2 w-40 h-40 object-cover border rounded-md"
                    />
                  </div>
                )}
                <label
                  htmlFor="file-upload"
                  className="flex items-center text-[#1F2937AB] hover:cursor-pointer justify-between w-full border p-3 mt-2 border-[#E1E1E1] h-[40px] rounded-md active:outline-none focus:outline-none active:border-[#D9DCB0] focus:border-[#D9DCB0]"
                >
                  <span>Upload</span>
                  <LuUpload className="text-[#1F2937AB] ml-2" />
                </label>
                {errors.image && (
                  <p className="text-errorcolor text-sm mt-1">
                    {errors.image.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-roboto-mono font-medium text-gray-700 mb-1">
              Status
            </label>
            <div className="relative flex items-center">
              <input
                type="text"
                value={isActive ? "Is Active" : "Inactive"}
                readOnly
                className="w-full px-3 py-2 text-sm text-[#1F2937AB] border border-[#E1E1E1] h-[40px] rounded-md outline-none focus:border-[#D9DCB0] pr-16"
              />
              <div className="absolute right-4">
                <ToggleSwitch
                  checked={isActive}
                  onChange={handleToggleChange}
                  size="medium"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-4">
            <button
              type="submit"
              className="items-center justify-center gap-3 bg-bgprimary rounded-lg px-10 py-5 font-quicksand text-black font-semibold text-[14px] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DestinationPopup;  